import { formatBytes } from '@/utils/format.utils';
import type { BrowserInfo, StorageEstimate } from '@/types/Diagnostics';
import type { LogEntry } from '@/types/LogEntry';
import type { P2PDiagnostics } from '@/types/P2PDiagnostics';

const PERCENT_MULTIPLIER = 100;
const PERCENT_PRECISION = 10;

interface DiagnosticsReportInput {
  storageEstimate: StorageEstimate | null;
  browserInfo: BrowserInfo | null;
  lastRefreshedAt: number | null;
}

interface FullReportInput extends DiagnosticsReportInput {
  logEntries: LogEntry[];
  p2pDiagnostics: P2PDiagnostics | null;
}

export function percentFree(estimate: StorageEstimate | null): number {
  if (estimate === null || estimate.quotaBytes <= 0) return 0;
  const free = estimate.quotaBytes - estimate.usageBytes;
  const raw = (free / estimate.quotaBytes) * PERCENT_MULTIPLIER;
  return Math.round(raw * PERCENT_PRECISION) / PERCENT_PRECISION;
}

function buildStorageBlock(estimate: StorageEstimate | null): string {
  if (estimate === null) return 'Storage\n  (unavailable)';
  return [
    'Storage',
    `  Used: ${formatBytes(estimate.usageBytes)}`,
    `  Quota: ${formatBytes(estimate.quotaBytes)}`,
    `  Free: ${percentFree(estimate)}%`,
  ].join('\n');
}

function buildBrowserBlock(info: BrowserInfo | null): string {
  if (info === null) return 'Environment\n  (unavailable)';
  return [
    'Environment',
    `  User Agent: ${info.userAgent}`,
    `  Language: ${info.language}`,
    `  Viewport: ${info.viewportWidth}x${info.viewportHeight}`,
    `  Online: ${info.online ? 'yes' : 'no'}`,
  ].join('\n');
}

function buildLogsBlock(logEntries: LogEntry[]): string {
  if (logEntries.length === 0) return 'Logs\n  (no entries)';
  const lines = logEntries.map(
    (entry) =>
      `  ${new Date(entry.timestamp).toISOString()} [${entry.level.toUpperCase()}] ` +
      `[${entry.namespace}] ${entry.message}`
  );
  return ['Logs', ...lines].join('\n');
}

function buildP2PBlock(p2pDiagnostics: P2PDiagnostics | null): string {
  if (p2pDiagnostics === null) return 'P2P\n  (not initialized)';

  const lines = [
    'P2P',
    `  Peers: ${p2pDiagnostics.peerCount}`,
    `  Helia pins: ${p2pDiagnostics.pinCount ?? 'n/a'}`,
    `  Broker: ${p2pDiagnostics.brokerStatus}`,
  ];

  if (p2pDiagnostics.multiaddrs.length > 0) {
    lines.push('  Multiaddrs:');
    for (const address of p2pDiagnostics.multiaddrs) lines.push(`    ${address}`);
  }

  if (p2pDiagnostics.peers.length > 0) {
    lines.push('  Connected peers:');
    for (const peer of p2pDiagnostics.peers) {
      const flags = [peer.isDirect ? 'direct' : '', peer.isRelay ? 'relay' : '']
        .filter((flag) => flag.length > 0)
        .join(',');
      lines.push(`    ${peer.peerId} ${peer.direction}${flags.length > 0 ? ` (${flags})` : ''}`);
      lines.push(`      ${peer.address}`);
    }
  }

  return lines.join('\n');
}

function reportHeader(lastRefreshedAt: number | null): string {
  const generatedAt =
    lastRefreshedAt === null ? 'unknown' : new Date(lastRefreshedAt).toISOString();
  return ['PortalSIX Diagnostics Report', `Generated: ${generatedAt}`].join('\n');
}

export function buildDiagnosticsReport(input: DiagnosticsReportInput): string {
  return [
    reportHeader(input.lastRefreshedAt),
    '',
    buildBrowserBlock(input.browserInfo),
    '',
    buildStorageBlock(input.storageEstimate),
  ].join('\n');
}

export function buildLogsReport(logEntries: LogEntry[], lastRefreshedAt: number | null): string {
  return [reportHeader(lastRefreshedAt), '', buildLogsBlock(logEntries)].join('\n');
}

export function buildP2PReport(
  p2pDiagnostics: P2PDiagnostics | null,
  lastRefreshedAt: number | null
): string {
  return [reportHeader(lastRefreshedAt), '', buildP2PBlock(p2pDiagnostics)].join('\n');
}

export function buildFullReport(input: FullReportInput): string {
  return [
    reportHeader(input.lastRefreshedAt),
    '',
    buildBrowserBlock(input.browserInfo),
    '',
    buildStorageBlock(input.storageEstimate),
    '',
    buildLogsBlock(input.logEntries),
    '',
    buildP2PBlock(input.p2pDiagnostics),
  ].join('\n');
}
